'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import Icon from '@/components/ui/AppIcon';

interface SignUpFormValues {
  fullName: string;
  email: string;
  phone: string;
  role: string;
  zone: string;
  centreName: string;
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const zones = ['Maharashtra', 'Gujarat', 'Rajasthan', 'Delhi NCR', 'Karnataka', 'Tamil Nadu', 'Kerala', 'West Bengal', 'Uttar Pradesh', 'Madhya Pradesh', 'Punjab', 'Haryana'];
const roles = ['Centre Coordinator', 'State Coordinator', 'Zone Coordinator', 'Volunteer'];

interface SignUpFormProps {
  onSwitchToLogin: () => void;
}

export default function SignUpForm({ onSwitchToLogin }: SignUpFormProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<SignUpFormValues>();

  const password = watch('password');

  // Backend integration point: POST /api/auth/register
  const onSubmit = async (_data: SignUpFormValues) => {
    setIsLoading(true);
    await new Promise((r) => setTimeout(r, 1500));
    setIsLoading(false);
    setSuccess(true);
  };

  if (success) {
    return (
      <div className="flex flex-col items-center text-center py-8 space-y-4 animate-fade-in">
        <div className="w-16 h-16 rounded-full bg-[var(--success-bg)] flex items-center justify-center">
          <Icon name="CheckCircleIcon" size={32} variant="outline" className="text-[var(--success)]" />
        </div>
        <div>
          <h2 className="text-xl font-extrabold text-foreground">Registration Submitted</h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xs">
            Your registration request has been sent to the state coordinator for approval. You will receive an email once approved.
          </p>
        </div>
        <button onClick={onSwitchToLogin} className="btn-primary mt-2">
          <Icon name="ArrowRightOnRectangleIcon" size={16} variant="outline" />
          Back to Sign In
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5">
        <h1 className="text-2xl font-extrabold text-foreground">Register Your Centre</h1>
        <p className="text-sm text-muted-foreground mt-1">Join the Respecting Mother Nature campaign</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
        <div>
          <label className="form-label" htmlFor="signup-name">Full Name</label>
          <input
            id="signup-name"
            type="text"
            autoComplete="name"
            placeholder="e.g. Sunita Devi Sharma"
            className={`form-input ${errors.fullName ? 'form-input-error' : ''}`}
            {...register('fullName', { required: 'Full name is required', minLength: { value: 3, message: 'Name must be at least 3 characters' } })}
          />
          {errors.fullName && <p className="form-error">{errors.fullName.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label" htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              autoComplete="email"
              placeholder="name@bk.org"
              className={`form-input ${errors.email ? 'form-input-error' : ''}`}
              {...register('email', {
                required: 'Email is required',
                pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
              })}
            />
            {errors.email && <p className="form-error">{errors.email.message}</p>}
          </div>
          <div>
            <label className="form-label" htmlFor="signup-phone">Mobile Number</label>
            <input
              id="signup-phone"
              type="tel"
              autoComplete="tel"
              placeholder="+91 98765 43210"
              className={`form-input ${errors.phone ? 'form-input-error' : ''}`}
              {...register('phone', {
                required: 'Mobile number is required',
                pattern: { value: /^[+\d\s\-()]{8,15}$/, message: 'Enter a valid mobile number' },
              })}
            />
            {errors.phone && <p className="form-error">{errors.phone.message}</p>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label" htmlFor="signup-role">Role</label>
            <div className="relative">
              <select
                id="signup-role"
                className={`form-input appearance-none pr-8 ${errors.role ? 'form-input-error' : ''}`}
                {...register('role', { required: 'Please select a role' })}
              >
                <option value="">Select role</option>
                {roles.map((r) => (
                  <option key={`role-opt-${r}`} value={r}>{r}</option>
                ))}
              </select>
              <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            {errors.role && <p className="form-error">{errors.role.message}</p>}
          </div>
          <div>
            <label className="form-label" htmlFor="signup-zone">Zone / State</label>
            <div className="relative">
              <select
                id="signup-zone"
                className={`form-input appearance-none pr-8 ${errors.zone ? 'form-input-error' : ''}`}
                {...register('zone', { required: 'Please select a zone' })}
              >
                <option value="">Select zone</option>
                {zones.map((z) => (
                  <option key={`zone-opt-${z}`} value={z}>{z}</option>
                ))}
              </select>
              <Icon name="ChevronDownIcon" size={14} variant="outline" className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            </div>
            {errors.zone && <p className="form-error">{errors.zone.message}</p>}
          </div>
        </div>

        <div>
          <label className="form-label" htmlFor="signup-centre">Centre Name</label>
          <p className="form-helper -mt-0.5 mb-1">Enter the full name of your Brahma Kumaris centre</p>
          <input
            id="signup-centre"
            type="text"
            placeholder="e.g. Pune Shivajinagar BK Centre"
            className={`form-input ${errors.centreName ? 'form-input-error' : ''}`}
            {...register('centreName', { required: 'Centre name is required' })}
          />
          {errors.centreName && <p className="form-error">{errors.centreName.message}</p>}
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="form-label" htmlFor="signup-password">Password</label>
            <div className="relative">
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Min. 8 characters"
                className={`form-input pr-10 ${errors.password ? 'form-input-error' : ''}`}
                {...register('password', {
                  required: 'Password is required',
                  minLength: { value: 8, message: 'Min. 8 characters' },
                  pattern: { value: /(?=.*[A-Z])(?=.*\d)/, message: 'Include 1 uppercase + 1 number' },
                })}
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showPassword ? 'Hide' : 'Show'}>
                <Icon name={showPassword ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
              </button>
            </div>
            {errors.password && <p className="form-error">{errors.password.message}</p>}
          </div>
          <div>
            <label className="form-label" htmlFor="signup-confirm">Confirm Password</label>
            <div className="relative">
              <input
                id="signup-confirm"
                type={showConfirm ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Repeat password"
                className={`form-input pr-10 ${errors.confirmPassword ? 'form-input-error' : ''}`}
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === password || 'Passwords do not match',
                })}
              />
              <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" aria-label={showConfirm ? 'Hide' : 'Show'}>
                <Icon name={showConfirm ? 'EyeSlashIcon' : 'EyeIcon'} size={16} variant="outline" />
              </button>
            </div>
            {errors.confirmPassword && <p className="form-error">{errors.confirmPassword.message}</p>}
          </div>
        </div>

        <div className="flex items-start gap-2">
          <input
            id="signup-terms"
            type="checkbox"
            className="rounded border-border accent-primary mt-0.5"
            {...register('agreeTerms', { required: 'You must agree to the terms' })}
          />
          <div>
            <label htmlFor="signup-terms" className="text-sm text-muted-foreground cursor-pointer leading-relaxed">
              I agree to the{' '}
              <span className="text-primary font-medium hover:underline cursor-pointer">Terms of Service</span>
              {' '}and{' '}
              <span className="text-primary font-medium hover:underline cursor-pointer">Privacy Policy</span>
            </label>
            {errors.agreeTerms && <p className="form-error">{errors.agreeTerms.message}</p>}
          </div>
        </div>

        <button type="submit" disabled={isLoading} className="btn-primary w-full py-3">
          {isLoading ? (
            <>
              <Icon name="ArrowPathIcon" size={16} variant="outline" className="animate-spin" />
              Submitting registration...
            </>
          ) : (
            <>
              <Icon name="UserPlusIcon" size={16} variant="outline" />
              Register Centre
            </>
          )}
        </button>
      </form>
    </div>
  );
}